import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminVippsDecide, adminVippsRequests, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const DecideDialog = ({ item, mode, onOpenChange, onConfirm, loading }) => {
  const [note, setNote] = useState("");
  const reject = mode === "reject";
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="vipps-decide-dialog">
        <DialogHeader>
          <DialogTitle>{reject ? "Avvis Vipps-oppsettet" : `Godkjenn Vipps ${item?.request?.vipps_number}`}</DialogTitle>
          <DialogDescription>{reject ? "Arrangøren får grunngjevinga di på e-post og kan sende ein ny førespurnad." : `Nye bestillingar hos ${item?.organizer_name} får betalingsinstruks med dette nummeret${item?.request?.vipps_link ? " – og «Opne Vipps»-knapp frå lenkja" : ""}. Sjekk at nummeret høyrer til arrangøren.`}</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={reject ? "Kvifor? (obligatorisk)" : "Merknad til arrangøren (valfritt)"} className="bg-background" data-testid="vipps-decide-note" />
        <DialogFooter>
          <Button variant="outline" className="bg-transparent" onClick={() => onOpenChange(null)} data-testid="vipps-decide-cancel">Avbryt</Button>
          <Button variant={reject ? "destructive" : "default"} disabled={loading || (reject && note.trim().length < 5)} onClick={() => onConfirm(note)} data-testid="vipps-decide-confirm">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {reject ? "Avvis" : "Godkjenn"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Top of Billettar → Arrangørar: organizers asking to receive ticket money on a (new) Vipps number. */
export const VippsRequests = () => {
  const qc = useQueryClient();
  const [decide, setDecide] = useState(null); // { item, mode }
  const { data } = useQuery({ queryKey: ["admin-vipps-requests"], queryFn: adminVippsRequests, staleTime: 30_000 });
  const m = useMutation({
    mutationFn: ({ item, mode, note }) => adminVippsDecide(item.organizer_id, mode, note),
    onSuccess: (_, { mode }) => { toast.success(mode === "approve" ? "Vippsnummeret er godkjent og teke i bruk" : "Førespurnaden er avvist"); setDecide(null); qc.invalidateQueries({ queryKey: ["admin-vipps-requests"] }); qc.invalidateQueries({ queryKey: ["admin-organizers"] }); qc.invalidateQueries({ queryKey: ["admin-dashboard"] }); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!data?.length) return null;
  return (
    <section className="mb-5 rounded-[14px] border border-sol/50 bg-[hsl(var(--status-pending-bg)/0.5)] p-4" data-testid="vipps-requests">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sol">Vipps-oppsett frå arrangørar</p>
      <ul className="mt-2 divide-y divide-border">
        {data.map((it) => (
          <li key={it.organizer_id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`vipps-request-${it.organizer_id}`}>
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-card"><Smartphone className="h-4 w-4" /></span>
              <div className="min-w-0 text-[13.5px]">
                <p className="font-medium">{it.organizer_name} ber om <span className="tabular">Vipps {it.request.vipps_number}</span>{it.request.vipps_link ? " · med lenkje" : ""}</p>
                <p className="mt-0.5 text-[12.5px] text-muted-foreground">I dag: {it.current.number ? `Vipps ${it.current.number}` : "ikkje registrert"} · sendt {fmtDateTime(it.request.requested_at)}{it.request.note ? ` · «${it.request.note}»` : ""}</p>
                {it.request.vipps_link && <p className="mt-0.5 truncate text-[12px] text-muted-foreground" data-testid={`vipps-request-link-${it.organizer_id}`}>{it.request.vipps_link}</p>}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" className="h-9 bg-transparent" onClick={() => setDecide({ item: it, mode: "reject" })} data-testid={`vipps-reject-${it.organizer_id}`}><X className="h-4 w-4" /> Avvis</Button>
              <Button className="h-9" onClick={() => setDecide({ item: it, mode: "approve" })} data-testid={`vipps-approve-${it.organizer_id}`}><Check className="h-4 w-4" /> Godkjenn</Button>
            </div>
          </li>
        ))}
      </ul>
      <DecideDialog key={`${decide?.item?.organizer_id || ""}-${decide?.mode || ""}`} item={decide?.item} mode={decide?.mode} onOpenChange={() => setDecide(null)} loading={m.isPending} onConfirm={(note) => m.mutate({ ...decide, note })} />
    </section>
  );
};
